import { format } from 'date-fns';
import addDays from 'date-fns/addDays';
import isSameDay from 'date-fns/isSameDay';
import { ptBR } from 'date-fns/locale';
import { Unit } from '../components/ServiceItem';
import { Rule } from '../models/Product';
import { WorkHours } from '../models/UnitInfo';
import { getRangeDatesRuleId } from './catalogRules';

export const excludeDates = (dates: Date[], initialDate: Date, finalDate: Date) => {
  let auxDate = initialDate;
  const excludedDates: Date[] = [];

  while (!isSameDay(auxDate, finalDate)) {
    const isExcluded = dates.reduce((acc, nextDate) => {
      return !isSameDay(nextDate, auxDate) && acc;
    }, true);
    if (isExcluded) {
      excludedDates.push(auxDate);
    }
    auxDate = addDays(auxDate, 1);
  }
  excludedDates.push(finalDate);
  return excludedDates;
};

export const getRangeDateFromToday = (days: number) => {
  const dateNow = new Date();
  const rangeDateEnd = new Date();
  return {
    today: dateNow,
    endDate: new Date(rangeDateEnd.setDate(rangeDateEnd.getDate() + days))
  };
};

export const daysInMonth = (month: number, year: number) => {
  return new Date(year, month, 0).getDate();
};

export const daysToEndMonth = (date: Date) => {
  return daysInMonth(date.getMonth() + 1, date.getFullYear());
};

export const dateToISoString = (date: Date) => {
  return date.toISOString().split('T')[0];
};

export const formatDatetime = (time: Date) => {
  const dateString = `${format(time, 'ccc', {
    locale: ptBR
  }).slice(0, 3)}, ${format(time, 'dd/MM/yyyy')}`;
  const timeString = format(time, 'HH:mm');
  const stringDateHour = `${dateString} às ${timeString}`;
  return stringDateHour.charAt(0).toUpperCase() + stringDateHour.slice(1);
};

export const formatDatetime1 = (time: Date) => {
  return format(time, "iii, dd 'de' LLL 'às' HH:mm", {
    locale: ptBR
  });
};

export const unitWorkDaysString = (unit: Unit): string[] => {
  const workDaysStrings: string[] = [];

  const daysOfTheWeek = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

  const workDays: boolean[] = [];
  workDays.push(false);
  workDays.push(unit.business_unit_work_hour.monday);
  workDays.push(unit.business_unit_work_hour.tuesday);
  workDays.push(unit.business_unit_work_hour.wednesday);
  workDays.push(unit.business_unit_work_hour.thursday);
  workDays.push(unit.business_unit_work_hour.friday);
  workDays.push(false);

  const emptyArray: number[] = [];
  const startingDays = workDays.reduce((acc, value, index, arr) => {
    if (index === 0) {
      const previousDayValue = arr[arr.length - 1];
      if (value && !previousDayValue) return [index];
      return [];
    } else {
      const previousDayValue = arr[index - 1];
      if (value && !previousDayValue) return [...acc, index];
      return [...acc];
    }
  }, emptyArray);

  const endingDays = workDays.reduce((acc, value, index, arr) => {
    if (index === 0) {
      const previousIndex = 6;
      const previousDayValue = arr[previousIndex];
      if (!value && previousDayValue) return [previousIndex];
      return [];
    } else {
      const previousIndex = index - 1;
      const previousDayValue = arr[previousIndex];
      if (!value && previousDayValue) return [...acc, previousIndex];
      return [...acc];
    }
  }, emptyArray);

  if (startingDays.length === 1 && endingDays.length === 1) {
    const startingDay = startingDays[0];
    const endingDay = endingDays[0];
    let daysString;
    if (startingDay === endingDay) {
      daysString = `${daysOfTheWeek[startingDay].charAt(0).toUpperCase() + daysOfTheWeek[startingDay].slice(1)}`;
    } else {
      daysString = `${daysOfTheWeek[startingDay].charAt(0).toUpperCase() + daysOfTheWeek[startingDay].slice(1)} a ${
        daysOfTheWeek[endingDay]
      }`;
    }
    const hoursString = `${unit.business_unit_work_hour.work_hour_start} às ${unit.business_unit_work_hour.work_hour_end}`;
    workDaysStrings.push(`${daysString}: ${hoursString}`);
  } else if (startingDays.length > 1 && endingDays.length > 1) {
    const workDaysString = workDays
      .map((value, index) => {
        if (value) return index;
        return -1;
      })
      .filter(index => index !== -1)
      .map(index => daysOfTheWeek[index])
      .reverse()
      .reduce((previous, current, index) => {
        if (index === 0) return current;
        if (index === 1) return `${current} e ${previous}`;
        return `${current}, ${previous}`;
      }, '');
    const daysStringUppercase = workDaysString.charAt(0).toUpperCase() + workDaysString.slice(1);
    const hoursString = `${unit.business_unit_work_hour.work_hour_start} às ${unit.business_unit_work_hour.work_hour_end}`;
    workDaysStrings.push(`${daysStringUppercase}: ${hoursString}`);
  }

  const weekend =
    !!unit.business_unit_work_hour.different_work_hour_start && !!unit.business_unit_work_hour.different_work_hour_end;
  if (weekend) {
    const hoursString = `${unit.business_unit_work_hour.different_work_hour_start} às ${unit.business_unit_work_hour.different_work_hour_end}`;
    if (unit.business_unit_work_hour.saturday && unit.business_unit_work_hour.sunday) {
      workDaysStrings.push(`Sábado e domingo: ${hoursString}`);
    } else if (unit.business_unit_work_hour.saturday) {
      workDaysStrings.push(`Sábado: ${hoursString}`);
    } else if (unit.business_unit_work_hour.sunday) {
      workDaysStrings.push(`Domingo: ${hoursString}`);
    }
  }

  const holidays = unit.business_unit_work_hour.holiday;
  if (holidays) {
    const hoursString = `${unit.business_unit_work_hour.holiday_work_hour_start} às ${unit.business_unit_work_hour.holiday_work_hour_end}`;
    workDaysStrings.push(`Feriados: ${hoursString}`);
  }

  return workDaysStrings;
};

export const getRangeDatesOfRule = (rules: Rule[], firstAppointment?: boolean): number => {
  if (firstAppointment) return 60;
  const rangeDateRule = rules.find(rule => rule.rule_id == getRangeDatesRuleId());
  if (rangeDateRule?.value) return Number(rangeDateRule.value);

  return 60;
};

export const unitWorkHoursString = (workHour: WorkHours): string[] => {
  const workDaysStrings: string[] = [];

  const daysOfTheWeek = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

  const workDays: boolean[] = [];
  workDays.push(false);
  workDays.push(workHour.weekdays.monday);
  workDays.push(workHour.weekdays.tuesday);
  workDays.push(workHour.weekdays.wednesday);
  workDays.push(workHour.weekdays.thursday);
  workDays.push(workHour.weekdays.friday);
  workDays.push(false);

  const emptyArray: number[] = [];
  const startingDays = workDays.reduce((acc, value, index, arr) => {
    if (index === 0) {
      const previousDayValue = arr[arr.length - 1];
      if (value && !previousDayValue) return [index];
      return [];
    } else {
      const previousDayValue = arr[index - 1];
      if (value && !previousDayValue) return [...acc, index];
      return [...acc];
    }
  }, emptyArray);

  const endingDays = workDays.reduce((acc, value, index, arr) => {
    if (index === 0) {
      const previousIndex = 6;
      const previousDayValue = arr[previousIndex];
      if (!value && previousDayValue) return [previousIndex];
      return [];
    } else {
      const previousIndex = index - 1;
      const previousDayValue = arr[previousIndex];
      if (!value && previousDayValue) return [...acc, previousIndex];
      return [...acc];
    }
  }, emptyArray);

  if (startingDays.length === 1 && endingDays.length === 1) {
    const startingDay = startingDays[0];
    const endingDay = endingDays[0];
    let daysString;
    if (startingDay === endingDay) {
      daysString = `${daysOfTheWeek[startingDay].charAt(0).toUpperCase() + daysOfTheWeek[startingDay].slice(1)}`;
    } else {
      daysString = `${daysOfTheWeek[startingDay].charAt(0).toUpperCase() + daysOfTheWeek[startingDay].slice(1)} a ${
        daysOfTheWeek[endingDay]
      }`;
    }
    const hoursString = `${workHour.start} às ${workHour.end}`;
    workDaysStrings.push(`${daysString}: ${hoursString}`);
  } else if (startingDays.length > 1 && endingDays.length > 1) {
    const workDaysString = workDays
      .map((value, index) => {
        if (value) return index;
        return -1;
      })
      .filter(index => index !== -1)
      .map(index => daysOfTheWeek[index])
      .reverse()
      .reduce((previous, current, index) => {
        if (index === 0) return current;
        if (index === 1) return `${current} e ${previous}`;
        return `${current}, ${previous}`;
      }, '');
    const daysStringUppercase = workDaysString.charAt(0).toUpperCase() + workDaysString.slice(1);
    const hoursString = `${workHour.start} às ${workHour.end}`;
    workDaysStrings.push(`${daysStringUppercase}: ${hoursString}`);
  }

  const weekend = !!workHour.different.start && !!workHour.different.end;
  if (weekend) {
    const hoursString = `${workHour.different.start} às ${workHour.different.end}`;
    if (workHour.weekends.saturday && workHour.weekends.sunday) {
      workDaysStrings.push(`Sábado e domingo: ${hoursString}`);
    } else if (workHour.weekends.saturday) {
      workDaysStrings.push(`Sábado: ${hoursString}`);
    } else if (workHour.weekends.sunday) {
      workDaysStrings.push(`Domingo: ${hoursString}`);
    }
  }

  const holidays = workHour.holiday.is_holiday;
  if (holidays) {
    const hoursString = `${workHour.holiday.hours.start} às ${workHour.holiday.hours.end}`;
    workDaysStrings.push(`Feriados: ${hoursString}`);
  }

  return workDaysStrings;
};
